import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import MessageThread from "@/components/MessageThread";
import ClientHabitGrid from "@/components/ClientHabitGrid";
import ClientVitalsView from "@/components/ClientVitalsView";
import ClientGoalsView from "@/components/ClientGoalsView";
import ExerciseProgress from "@/components/ExerciseProgress";
import NudgeClientButton from "@/components/NudgeClientButton";
import CoachCancelClientButton from "@/components/CoachCancelClientButton";
import SessionFeedback from "@/components/SessionFeedback";
import OnboardingReview from "@/components/OnboardingReview";
import WeeklyHabitReport from "@/components/WeeklyHabitReport";
import SleepHistory from "@/components/SleepHistory";
import ActivityLog from "@/components/ActivityLog";
import { ArrowLeft, PencilSimple, Plus, ForkKnife } from "@phosphor-icons/react";

export default function CoachClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get(`/coach/clients/${id}`);
      setData(data);
    } catch (e) {
      setErr(apiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="p-10 id-label id-pulse">Loading client...</div>;
  const { client, plan, assignments, progress, workout_logs, programs: clientPrograms, goals, nutrition_prefs, body_latest } = data;
  const programNameById = Object.fromEntries(clientPrograms.map((p) => [p.id, p.name]));
  const active = assignments[0];

  // Membership snapshot for the coach cancel modal — derived from the client
  // user record so the modal shows the correct current state without a second
  // fetch. Matches the shape returned by /api/payments/membership.
  const membership = {
    status: client.subscription_status || (client.subscription_active_until ? "active_one_time" : "inactive"),
    active: Boolean(client.subscription_active_until || client.subscription_status === "active" || client.subscription_status === "trialing"),
    cancel_at_period_end: Boolean(client.subscription_cancel_at_period_end),
    has_subscription: Boolean(client.stripe_subscription_id),
    days_remaining: null,
  };

  const chartData = progress.map((p) => ({
    date: (p.date || "").slice(5, 10),
    weight: p.weight_kg,
  }));

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <Link to="/coach/clients" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.2em] mb-6" data-testid="back-to-clients">
        <ArrowLeft size={14} weight="bold" /> Back to Roster
      </Link>

      <div className="id-label mb-3">— Client File —</div>
      <h1 className="id-h1 mb-1" data-testid="client-name">{client.name}</h1>
      <div className="text-zinc-500 font-manrope text-sm mb-6">{client.email}</div>

      {Array.isArray(client.controlling_lies_labels) && client.controlling_lies_labels.length > 0 ? (
        <div className="border-l-2 border-[#FF3333] pl-4 mb-10 max-w-2xl" data-testid="controlling-lie">
          <div className="id-label text-[#FF3333] mb-2">Controlling {client.controlling_lies_labels.length > 1 ? "lies" : "lie"}</div>
          <div className="space-y-1.5">
            {client.controlling_lies_labels.map((l, i) => (
              <div key={i} className="font-oswald text-lg uppercase tracking-wide text-white leading-tight">
                &ldquo;{l}&rdquo;
              </div>
            ))}
          </div>
          <div className="text-xs text-zinc-500 mt-2 font-manrope">The mindset blocks they identified in onboarding. Use these in your DMs.</div>
        </div>
      ) : client.controlling_lie_label && (
        <div className="border-l-2 border-[#FF3333] pl-4 mb-10 max-w-2xl" data-testid="controlling-lie">
          <div className="id-label text-[#FF3333] mb-1">Controlling lie</div>
          <div className="font-oswald text-xl uppercase tracking-wide text-white leading-tight">
            &ldquo;{client.controlling_lie_label}&rdquo;
          </div>
          <div className="text-xs text-zinc-500 mt-1 font-manrope">The mindset block they identified in onboarding. Use this in your DMs.</div>
        </div>
      )}

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <div className="id-card lg:col-span-2">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="id-label">Active Plan</div>
            <Link
              to={`/coach/clients/${client.id}/plan`}
              data-testid="open-plan-editor"
              className="id-btn-primary text-xs"
            >
              {plan ? (<><PencilSimple size={14} weight="bold" /> Edit Plan</>) : (<><Plus size={14} weight="bold" /> Create Plan</>)}
            </Link>
            <Link
              to={`/coach/clients/${client.id}/meals`}
              data-testid="open-meals-editor"
              className="id-btn-secondary text-xs ml-2"
            >
              <ForkKnife size={14} weight="bold" /> Meal Plan
            </Link>
            <NudgeClientButton clientId={client.id} clientName={client.name} />
            <CoachCancelClientButton
              clientId={client.id}
              clientName={client.name}
              membership={membership}
              onCancelled={load}
            />
          </div>

          {/* Onboarding review — every answer the client submitted + starting photos */}
          <OnboardingReview
            client={client}
            goals={goals}
            nutrition_prefs={nutrition_prefs}
            body_latest={body_latest}
          />

          {plan ? (
            <div data-testid="active-plan">
              <div className="font-oswald text-2xl uppercase tracking-wide">{plan.name}</div>
              <div className="text-xs text-zinc-500 mt-1 font-manrope">
                {plan.days?.length || 0} workout day{(plan.days?.length || 0) !== 1 ? "s" : ""} · {plan.weeks || 4} week{(plan.weeks || 1) !== 1 ? "s" : ""}
                {plan.updated_at && ` · Updated ${new Date(plan.updated_at).toLocaleDateString()}`}
              </div>
              {plan.description && <p className="text-zinc-400 text-sm mt-3 font-manrope">{plan.description}</p>}
              {plan.coach_notes && (
                <div className="mt-4 pt-4 border-t border-[#262626]">
                  <div className="id-label mb-2">Habits & Nutrition Notes</div>
                  <pre className="text-zinc-300 text-sm font-manrope whitespace-pre-wrap leading-relaxed">{plan.coach_notes}</pre>
                </div>
              )}
            </div>
          ) : active ? (
            <div data-testid="active-program" className="text-zinc-300">
              <div className="font-oswald text-2xl uppercase tracking-wide">{programNameById[active.program_id] || "Unknown"}</div>
              <div className="text-xs text-zinc-500 mt-1 font-manrope">Legacy template · Assigned {new Date(active.assigned_at).toLocaleDateString()}</div>
              <p className="text-zinc-500 text-xs mt-3 font-manrope">Click <span className="text-white">Edit Plan</span> to switch this client to a personalised plan.</p>
            </div>
          ) : (
            <div className="text-zinc-500 font-manrope" data-testid="no-plan">
              No plan yet. Click <span className="text-white">Create Plan</span> to build this client's workouts and habit notes from scratch.
            </div>
          )}
        </div>

        <div className="id-card">
          <div className="id-label mb-3">Sessions Logged</div>
          <div className="id-stat" data-testid="sessions-count">{workout_logs.length}</div>
          <div className="text-zinc-500 text-xs mt-2 font-manrope">
            Last: {workout_logs[0] ? new Date(workout_logs[0].logged_at).toLocaleDateString() : "—"}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <ClientGoalsView clientId={client.id} />
      </div>

      <div className="mb-6">
        <WeeklyHabitReport clientId={client.id} />
      </div>

      <div className="mb-6">
        <SleepHistory scope="client" clientId={client.id} />
      </div>

      <div className="mb-6">
        <ActivityLog scope="client" clientId={client.id} />
      </div>

      <div className="mb-6">
        <ClientHabitGrid clientId={client.id} />
      </div>

      <div className="mb-6">
        <ExerciseProgress fetchPath={`/coach/clients/${client.id}/progress/exercises`} />
      </div>

      <div className="mb-6">
        <ClientVitalsView clientId={client.id} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
        <div className="id-card">
          <div className="id-label mb-4">Body Weight (kg)</div>
          {chartData.length === 0 ? (
            <div className="text-zinc-500 font-manrope text-sm h-48 flex items-center justify-center">No progress entries yet.</div>
          ) : (
            <div className="h-48" data-testid="progress-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#262626" strokeDasharray="0" vertical={false} />
                  <XAxis dataKey="date" stroke="#525252" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                  <YAxis stroke="#525252" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 0, fontFamily: "Manrope" }} />
                  <Line type="monotone" dataKey="weight" stroke="#FF3333" strokeWidth={2} dot={{ fill: "#fff", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="id-card">
          <div className="id-label mb-4">Recent Sessions</div>
          {workout_logs.length === 0 ? (
            <div className="text-zinc-500 font-manrope text-sm">No sessions yet.</div>
          ) : (
            <ul className="divide-y divide-[#262626]" data-testid="sessions-list">
              {workout_logs.slice(0, 10).map((l) => (
                <SessionFeedback key={l.id} log={l} onDeleted={load} />
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="id-card">
        <div className="id-label mb-4">Message {client.name}</div>
        <MessageThread clientId={client.id} />
      </div>
    </div>
  );
}
